import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NoteDto } from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { notesKeys } from "./notes.keys";

const invalidateRelatedNotes = async (
  queryClient: ReturnType<typeof useQueryClient>,
  note: Partial<NoteDto> | undefined,
  fallbackPatientId?: string,
  fallbackAppointmentId?: string
) => {
  const patientId = note?.patientId ? note.patientId.toString() : fallbackPatientId;
  const appointmentId = note?.appointmentId ? note.appointmentId.toString() : fallbackAppointmentId;

  if (patientId) {
    await queryClient.invalidateQueries({
      queryKey: notesKeys.patientNotes.detail(patientId),
    });
  }

  if (appointmentId) {
    await queryClient.invalidateQueries({
      queryKey: notesKeys.appointmentNotes.detail(appointmentId),
    });
  }

  await queryClient.invalidateQueries({
    queryKey: notesKeys.patientNotes.all(),
  });
  await queryClient.invalidateQueries({
    queryKey: notesKeys.appointmentNotes.all(),
  });
};

export const useCreateNote = () => {
  const { notes } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, note }: { userId: string; note: NoteDto }) => {
      return await notes.postNote(userId, note);
    },
    onSuccess: async (_, variables) => {
      await invalidateRelatedNotes(queryClient, variables.note);
    },
    meta: {
      successMessage: "Hinweis erfolgreich gespeichert",
      errorMessage: "Fehler beim Speichern des Hinweises",
    },
  });
};

export const useUpdateNote = () => {
  const { notes } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, userId, note }: { noteId: string; userId: string; note: NoteDto }) => {
      // updateNote targets a persisted note: a real UUID id is required.
      return await notes.updateNote(noteId, userId, { ...note, id: note.id ?? noteId });
    },
    onSuccess: async (_, variables) => {
      await invalidateRelatedNotes(queryClient, variables.note);
    },
    meta: {
      successMessage: "Hinweis erfolgreich aktualisiert",
      errorMessage: "Fehler beim Aktualisieren des Hinweises",
    },
  });
};

export const useDeleteNote = () => {
  const { notes } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId }: { noteId: string; patientId?: string; appointmentId?: string }) => {
      return await notes.deleteNote(noteId);
    },
    onSuccess: async (_, variables) => {
      await invalidateRelatedNotes(
        queryClient,
        undefined,
        variables.patientId,
        variables.appointmentId
      );
    },
    meta: {
      successMessage: "Hinweis erfolgreich gelöscht",
      errorMessage: "Fehler beim Löschen des Hinweises",
    },
  });
};
